import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { CeldaTablero, EquipoTablero, PlantillaAsesora, SemanaMeta, Tablero, VentaTablero } from "../../../services/v2/prospects.service";
import { dinero } from "./salesHelpers";
import { corto, logoDe } from "./tableroView";
import "./tableroEquipos.css";

interface Props { campusId: number; t: Tablero; scope: "plantel" | "pais" | "empresa"; }

type Modo = "dia" | "semana" | "mes";
interface Fila { clave: string; nombre: string; foto: string | null; mat: number; monto: number; porDia: { [iso: string]: number }; claves: Set<string>; }
interface Tarjeta { equipo: EquipoTablero; filas: Fila[]; mat: number; monto: number; }

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const DIAS_CORTO = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const PLANTEL: { [k: number]: string } = { 1: "MTY", 2: "TRC", 3: "TGU", 4: "SPS", 5: "Online", 7: "SAL" };
const PAIS_NOMBRE: { [k: string]: string } = { hn: "Honduras", mx: "México" };
const pad = (n: number) => String(n).padStart(2, "0");
export const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const deIso = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const suma = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });
const iniciales = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const moneda = (pais: string) => (pais === "mx" ? "$" : "L");
const claveDe = (a: { userId: string; name: string; email?: string | null }) => a.email || (a.userId ? "id:" + a.userId : a.name.toLowerCase());
const paisDe = (v: VentaTablero): "hn" | "mx" => v.pais || (v.plantel === 1 || v.plantel === 2 ? "mx" : "hn");
/** "dom 1 – dom 8 nov" (con el mes de inicio si cambia). */
export const rangoTexto = (desde: string, hasta: string): string => {
	const a = deIso(desde), b = deIso(hasta);
	return `${DIAS_CORTO[a.getDay()]} ${a.getDate()}${a.getMonth() !== b.getMonth() ? " " + MESES[a.getMonth()].slice(0, 3) : ""} – ${DIAS_CORTO[b.getDay()]} ${b.getDate()} ${MESES[b.getMonth()].slice(0, 3)}`;
};

/**
 * Semanas de meta por defecto, la misma regla del servidor (VentasSemanas): 4 semanas de 7 días; los días que sobran de 28 se
 * reparten (el 1º a la semana 1, el 2º a la 3, el 3º a la 4). Sirve mientras llega el mes del servidor (que puede traerlas ajustadas).
 */
export const semanasPorDefecto = (mes: string): SemanaMeta[] => {
	const [y, m] = mes.split("-").map(Number);
	const ultimo = new Date(y, m, 0).getDate();
	const largos = [7, 7, 7, 7];
	[1, 3, 4].slice(0, Math.max(0, ultimo - 28)).forEach((semana) => { largos[semana - 1]++; });
	const out: SemanaMeta[] = [];
	let desde = 1;
	largos.forEach((largo, i) => {
		const hasta = i === 3 ? ultimo : desde + largo - 1;
		out.push({ n: i + 1, desde: `${mes}-${pad(desde)}`, hasta: `${mes}-${pad(hasta)}` });
		desde = hasta + 1;
	});
	return out;
};

/** Celdas del mes; si el servidor aún no las manda, se arman de la lista de ventas (sólo Supervisión la tiene completa). */
const celdasDe = (t: Tablero): CeldaTablero[] => {
	if (t.celdas) return t.celdas;
	const m: { [k: string]: CeldaTablero } = {};
	(t.ventas || []).forEach((v) => {
		if (v.noEsVenta) return;
		const dia = v.fecha.slice(0, 10), pais = paisDe(v);
		const partes = v.asesoras.length ? v.asesoras : [null];
		partes.forEach((a) => {
			const clave = a ? claveDe(a) : null;
			const k = dia + "|" + pais + "|" + v.grupo + "|" + (clave || "");
			const peso = v.valor / partes.length;
			const c = m[k] || (m[k] = { dia, pais, equipo: v.grupo, clave, asesora: a ? a.name : null, mat: 0, monto: 0 });
			c.mat += peso; c.monto += (v.monto || 0) * peso;
		});
	});
	return Object.values(m);
};

/**
 * Por equipo (Esteban, 4-nov-2026: "una vista per group daily y weekly de matrículas, al detalle"): lo que las líderes arman a mano
 * en WappTeam. Resumen del período por equipo y una tarjeta por equipo con toda su plantilla (también las que van en 0), día por
 * día en la semana. La semana es la semana de meta (4 por mes, de 7 días más los que sobran; ajustable por Dirección), no la del calendario. Al abrir una
 * asesora se ven sus ventas del período (solo quien ve la lista de ventas; la asesora, las suyas).
 */
const TableroEquipos: React.FC<Props> = ({ campusId, t, scope }) => {
	const http = HttpManager.getInstance();
	const [modo, setModo] = useState<Modo>("dia");
	const hoy = iso(new Date());
	const dentroDelMes = (d: string) => d.slice(0, 7) === t.mes;
	const ultimoConVentas = t.dias.length ? t.mes + "-" + pad(t.dias[t.dias.length - 1].dia) : t.mes + "-01";
	const [dia, setDia] = useState<string>(dentroDelMes(hoy) ? hoy : ultimoConVentas);
	const [abierta, setAbierta] = useState<string | null>(null);
	// Otros meses que toca el período (una semana de meta que cruza de mes): se piden aparte y se guardan.
	const [extra, setExtra] = useState<{ [mes: string]: Tablero }>({});
	const pidiendo = useRef<Set<string>>(new Set());
	const [copiado, setCopiado] = useState(false);
	const asesora = !!t.soloMias || t.veLista === false;

	// Cambio de mes arriba: la fecha se va a ese mes (hoy si es el actual; si no, el último día con ventas).
	useEffect(() => { setDia(dentroDelMes(hoy) ? hoy : ultimoConVentas); setAbierta(null); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [t.mes]);

	const semanasDe = (mes: string): SemanaMeta[] => (mes === t.mes ? t.semanas : extra[mes]?.semanas) || semanasPorDefecto(mes);
	const semanaDe = (d: string): SemanaMeta => semanasDe(d.slice(0, 7)).find((s) => s.desde <= d && d <= s.hasta) || { n: 0, desde: d, hasta: d };

	const periodo = useMemo(() => {
		const d = deIso(dia);
		if (modo === "dia") return { desde: dia, hasta: dia, titulo: cap(DIAS[d.getDay()]) + " " + d.getDate() + " de " + MESES[d.getMonth()], sub: "" };
		if (modo === "semana") {
			const s = semanaDe(dia);
			const jue = deIso(s.desde);
			return { desde: s.desde, hasta: s.hasta, titulo: `Semana ${s.n} de ${MESES[Number(s.desde.slice(5, 7)) - 1]}`, sub: rangoTexto(s.desde, s.hasta), jue };
		}
		return { desde: t.mes + "-01", hasta: t.mes + "-" + pad(t.ultimoDia), titulo: t.mesTexto, sub: "" };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modo, dia, t.mes, t.mesTexto, t.ultimoDia, t.semanas, extra]);

	// Meses que toca el período y no están cargados (también el de la semana, por si Dirección la ajustó).
	useEffect(() => {
		const meses = Array.from(new Set([periodo.desde.slice(0, 7), periodo.hasta.slice(0, 7)])).filter((m) => m !== t.mes && !extra[m] && !pidiendo.current.has(m));
		meses.forEach((m) => {
			pidiendo.current.add(m);
			http.prospectsService.dashboard(campusId, m, scope === "plantel" ? null : scope)
				.then((r) => setExtra((e) => ({ ...e, [m]: r.data })))
				.catch(() => undefined)
				.finally(() => pidiendo.current.delete(m));
		});
	}, [periodo.desde, periodo.hasta, t.mes, extra, campusId, scope, http]);

	const dias = useMemo(() => { const out: string[] = []; for (let d = deIso(periodo.desde); iso(d) <= periodo.hasta; d = suma(d, 1)) out.push(iso(d)); return out; }, [periodo]);
	const fuentes = useMemo(() => [t, ...Object.values(extra).filter((x) => x.mes !== t.mes)], [t, extra]);
	const faltaMes = dias.some((d) => !fuentes.some((x) => x.mes === d.slice(0, 7)));

	const tarjetas = useMemo<Tarjeta[]>(() => {
		const enPeriodo = (d: string) => d >= periodo.desde && d <= periodo.hasta;
		// La plantilla de hoy (tb_zoho_users) manda: todas salen aunque vayan en 0 (Esteban, 4-nov-2026). La del mes mostrado primero.
		const plantilla: { [k: string]: PlantillaAsesora } = {};
		[...fuentes].reverse().forEach((x) => (x.plantilla || []).forEach((p) => { plantilla[p.pais + "|" + p.equipo + "|" + p.clave] = p; }));
		const porEquipo: { [k: string]: { [c: string]: Fila } } = {};
		const filaDe = (ke: string, clave: string, nombre: string, foto: string | null): Fila => {
			const filas = porEquipo[ke] || (porEquipo[ke] = {});
			return filas[clave] || (filas[clave] = { clave, nombre, foto, mat: 0, monto: 0, porDia: {}, claves: new Set(clave.includes("@") || clave.startsWith("id:") ? [clave] : []) });
		};
		Object.values(plantilla).forEach((p) => filaDe(p.pais + "|" + p.equipo, p.clave, p.asesora, p.foto));
		fuentes.forEach((x) => {
			const fotoDe: { [k: string]: string | null } = {};
			x.asesoras.forEach((a) => { fotoDe[a.pais + "|" + a.equipo + "|" + a.asesora] = a.foto; });
			celdasDe(x).forEach((c) => {
				if (!enPeriodo(c.dia)) return;
				const ke = c.pais + "|" + c.equipo;
				const nombre = c.asesora || "Sin asesora";
				const f = filaDe(ke, c.clave || nombre, nombre, fotoDe[ke + "|" + nombre] ?? null);
				// El nombre con el que vendió (el mismo del Top 5) por encima del de la plantilla.
				if (c.asesora) f.nombre = c.asesora;
				if (!f.foto && fotoDe[ke + "|" + nombre]) f.foto = fotoDe[ke + "|" + nombre];
				f.mat += c.mat; f.monto += c.monto; f.porDia[c.dia] = (f.porDia[c.dia] || 0) + c.mat;
			});
		});
		// Todos los equipos de la rueda, aunque vayan en 0; los demás (Kits, Dirección…) solo si vendieron en el período.
		const equipos = t.equipos.filter((e) => e.compite || Object.values(porEquipo[e.pais + "|" + e.equipo] || {}).some((f) => f.mat > 0));
		return equipos.map((e) => {
			const ke = e.pais + "|" + e.equipo;
			const lista = Object.values(porEquipo[ke] || {}).sort((a, b) => b.mat - a.mat || b.monto - a.monto || (a.nombre === "Sin asesora" ? 1 : b.nombre === "Sin asesora" ? -1 : a.nombre.localeCompare(b.nombre)));
			return { equipo: e, filas: lista, mat: lista.reduce((s, f) => s + f.mat, 0), monto: lista.reduce((s, f) => s + f.monto, 0) };
		}).sort((a, b) => b.mat - a.mat || b.monto - a.monto || a.equipo.orden - b.equipo.orden);
	}, [fuentes, periodo, t.equipos]);
	const total = tarjetas.reduce((s, c) => s + c.mat, 0);
	// La supervisora de cada equipo (Esteban, 4-nov-2026: "poner la foto de la super y el nombre arriba de cada equipo").
	const supervisorasDe = (c: Tarjeta): PlantillaAsesora[] => {
		const lista = (t.supervisoras || []).filter((p) => p.pais === c.equipo.pais && p.equipo === c.equipo.equipo);
		return lista.length ? lista : fuentes.flatMap((x) => (x.supervisoras || []).filter((p) => p.pais === c.equipo.pais && p.equipo === c.equipo.equipo)).slice(0, 2);
	};
	// Proyección de la semana de meta (Esteban, 4-nov-2026: "40 matrículas es la proyección semanal"): solo en la vista Semana.
	const proyeccionDe = (c: Tarjeta): number | null => {
		if (modo !== "semana") return null;
		const x = fuentes.find((f) => f.mes === periodo.desde.slice(0, 7)) || t;
		const m = (x.metasSemana || []).find((y) => y.pais === c.equipo.pais && y.equipo === c.equipo.equipo && y.desde === periodo.desde);
		return m ? m.meta : (c.equipo.metaSemanal ?? null);
	};
	const totalProyeccion = modo === "semana" ? tarjetas.reduce((s, c) => s + (proyeccionDe(c) || 0), 0) : 0;

	// Detalle de una asesora en el período: sus ventas (quien ve la lista; la asesora solo las suyas).
	const ventasDe = (c: Tarjeta, f: Fila): VentaTablero[] => fuentes.flatMap((x) => x.ventas || []).filter((v) => {
		const d = v.fecha.slice(0, 10);
		if (v.noEsVenta || d < periodo.desde || d > periodo.hasta || v.grupo !== c.equipo.equipo || paisDe(v) !== c.equipo.pais) return false;
		if (f.nombre === "Sin asesora") return v.asesoras.length === 0;
		return v.asesoras.some((a) => f.claves.has(claveDe(a)) || a.name === f.nombre);
	}).sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
	const hayLista = fuentes.some((x) => (x.ventas || []).length > 0);

	const mover = (n: number) => {
		setAbierta(null);
		if (modo === "semana") { const s = semanaDe(dia); setDia(n > 0 ? iso(suma(deIso(s.hasta), 1)) : iso(suma(deIso(s.desde), -1))); return; }
		setDia(iso(suma(deIso(dia), n)));
	};
	const irHoy = () => { setAbierta(null); setDia(hoy); };
	const copiar = () => {
		const lineas = [periodo.titulo + (periodo.sub ? " · " + periodo.sub : "") + " ✨", ""];
		tarjetas.forEach((c) => lineas.push(`${c.equipo.equipo}${scope === "empresa" ? " " + (PAIS_NOMBRE[c.equipo.pais] || "") : ""} ${num(c.mat)}`));
		lineas.push("", "Total: " + num(total));
		tarjetas.filter((c) => c.filas.length).forEach((c) => {
			lineas.push("", `EQUIPO ${c.equipo.equipo.toUpperCase()} · ${periodo.titulo}`);
			c.filas.forEach((f) => lineas.push(`${f.nombre} ${num(f.mat)}`));
			lineas.push(`Total ${num(c.mat)}`);
		});
		navigator.clipboard?.writeText(lineas.join("\n")).then(() => { setCopiado(true); window.setTimeout(() => setCopiado(false), 1800); }).catch(() => undefined);
	};
	const enPeriodoHoy = periodo.desde <= hoy && hoy <= periodo.hasta;

	return (
		<div className="te">
			<div className="te-barra">
				<div className="te-modos" role="tablist" aria-label="Período">
					{([["dia", "Día"], ["semana", "Semana"], ["mes", "Mes"]] as [Modo, string][]).map(([k, l]) => (
						<button key={k} type="button" role="tab" aria-selected={modo === k} className={"btn btn-sm rounded-pill " + (modo === k ? "btn-primary" : "btn-outline-primary")} onClick={() => { setModo(k); setAbierta(null); }}>{l}</button>
					))}
				</div>
				<div className="te-nav">
					{modo !== "mes" ? <button type="button" className="te-flecha" onClick={() => mover(-1)} aria-label={modo === "dia" ? "Día anterior" : "Semana anterior"}>‹</button> : null}
					<div className="te-titulo"><b>{periodo.titulo}</b>{periodo.sub ? <small>{periodo.sub}</small> : null}</div>
					{modo !== "mes" ? <button type="button" className="te-flecha" onClick={() => mover(1)} disabled={periodo.hasta >= hoy} aria-label={modo === "dia" ? "Día siguiente" : "Semana siguiente"}>›</button> : null}
					{modo !== "mes" && !enPeriodoHoy ? <button type="button" className="btn btn-link btn-sm" onClick={irHoy}>Hoy</button> : null}
				</div>
				{/* El texto para WappTeam lo arman las líderes, no las asesoras (Esteban, 4-nov-2026). */}
				{asesora ? null : <button type="button" className="btn btn-outline-primary btn-sm rounded-pill te-copiar" onClick={copiar}>{copiado ? "Copiado" : "Copiar Resumen"}</button>}
			</div>

			<div className="te-resumen">
				{tarjetas.map((c) => (
					<div key={c.equipo.pais + c.equipo.equipo} className={"te-chip" + (c.mat === 0 ? " cero" : "") + (proyeccionDe(c) !== null && c.mat > 0 && c.mat >= (proyeccionDe(c) || 0) ? " en-meta" : "")} style={{ "--col": c.equipo.color || "var(--wa-apagado)" } as React.CSSProperties}>
						<img src={logoDe(c.equipo)} alt="" /><span>{c.equipo.equipo}{scope === "empresa" ? <small> {PAIS_NOMBRE[c.equipo.pais]}</small> : null}</span><b className="pd-num">{num(c.mat)}{proyeccionDe(c) !== null ? <small> de {num(proyeccionDe(c) || 0)}</small> : null}</b>
					</div>
				))}
				<div className="te-chip te-total"><span>Total</span><b className="pd-num">{num(total)}{totalProyeccion ? <small> de {num(totalProyeccion)}</small> : null}</b></div>
				{faltaMes ? <span className="te-cargando">Cargando el otro mes…</span> : null}
			</div>

			<div className="te-tarjetas">
				{tarjetas.map((c) => {
					const e = c.equipo;
					const col = e.color || "var(--wa-apagado)";
					const conVentas = c.filas.filter((f) => f.mat > 0).length;
					return (
						<section key={e.pais + e.equipo} className="te-tarjeta" style={{ "--col": col } as React.CSSProperties}>
							<header>
								<img src={logoDe(e)} alt="" />
								<div className="te-eq"><b>{e.equipo}</b><small>{scope === "empresa" ? PAIS_NOMBRE[e.pais] + " · " : ""}{conVentas} de {c.filas.length} con ventas</small></div>
								<div className="te-cifra"><b className="pd-num">{num(c.mat)}{proyeccionDe(c) !== null ? <small className="te-de"> de {num(proyeccionDe(c) || 0)}</small> : null}</b><small>{dinero(c.monto, moneda(e.pais))}</small></div>
							</header>
							{supervisorasDe(c).length ? (
								<div className="te-super">
									{supervisorasDe(c).map((p) => (
										<div key={p.clave} className="te-super-p">
											{p.foto ? <img src={p.foto} alt="" /> : <span className="te-ini">{iniciales(p.asesora)}</span>}
											<span><small>Supervisora</small><b>{corto(p.asesora)}</b></span>
										</div>
									))}
								</div>
							) : null}
							{proyeccionDe(c) !== null ? (() => { const p = proyeccionDe(c) || 0; const pct = p > 0 ? Math.min(100, Math.round(100 * c.mat / p)) : 0; return (
								<div className={"te-barra-meta" + (p > 0 && c.mat >= p ? " lista" : "")} title={`Proyección de la semana: ${num(p)}`}><span style={{ width: pct + "%" }} /><i>{p > 0 ? pct + "% de la proyección" : "Sin proyección"}{p > 0 && c.mat < p ? ` · faltan ${num(p - c.mat)}` : p > 0 ? " · ¡en meta!" : ""}</i></div>
							); })() : null}
							{c.filas.length === 0 ? <div className="te-vacio">Sin asesoras en el equipo.</div> : (
								<div className="te-tabla-marco"><table className={"te-tabla" + (modo === "semana" ? " te-semana" : "")}>
									<thead>
										<tr>
											<th className="te-pos">#</th><th>Asesora</th>
											{modo === "semana" ? dias.map((d) => { const x = deIso(d); return <th key={d} className={"te-dia" + (d === hoy ? " hoy" : d > hoy ? " futuro" : "")}><span>{DIAS_CORTO[x.getDay()]}</span>{x.getDate()}</th>; }) : null}
											<th className="te-num">Mat.</th><th className="te-num te-monto">Monto</th>
										</tr>
									</thead>
									<tbody>
										{c.filas.map((f, i) => {
											const k = e.pais + "|" + e.equipo + "|" + f.clave;
											const abierto = abierta === k;
											const detalle = abierto ? ventasDe(c, f) : [];
											const expandible = hayLista && f.mat > 0;
											return (
												<React.Fragment key={k}>
													<tr className={(f.mat === 0 ? "cero" : "") + (abierto ? " abierta" : "") + (expandible ? " te-clic" : "")} onClick={expandible ? () => setAbierta(abierto ? null : k) : undefined} aria-expanded={expandible ? abierto : undefined}>
														<td className="te-pos pd-num">{i + 1}</td>
														<td className="te-nom">{f.foto ? <img src={f.foto} alt="" /> : <span className="te-ini">{iniciales(f.nombre)}</span>}<span>{corto(f.nombre)}</span></td>
														{modo === "semana" ? dias.map((d) => <td key={d} className={"te-dia pd-num" + (d > hoy ? " futuro" : "")}>{f.porDia[d] ? num(f.porDia[d]) : <i>·</i>}</td>) : null}
														<td className="te-num pd-num"><b>{num(f.mat)}</b></td>
														<td className="te-num te-monto pd-num">{f.monto ? dinero(f.monto, moneda(e.pais)) : ""}</td>
													</tr>
													{abierto ? (
														<tr className="te-detalle"><td colSpan={4 + (modo === "semana" ? dias.length : 0)}>
															{detalle.length === 0 ? <span className="apagado">Sin el detalle de estas ventas.</span> : (
																<ul>
																	{detalle.map((v) => (
																		<li key={v.clave}>
																			<span className="te-cuando pd-num">{modo === "dia" ? (v.soloDia ? "" : horaDe(v.fecha)) : diaCorto(v.fecha)}</span>
																			<span className="te-cliente">{v.cliente}</span>
																			<span className="te-curso">{v.curso}{t.planteles.length > 1 ? " · " + (PLANTEL[v.plantel] || v.plantel) : ""}{v.valor !== 1 ? " · cuenta ½" : ""}{v.asesoras.length > 1 ? " · compartida" : ""}</span>
																			<span className="te-mo pd-num">{v.monto ? dinero(v.monto, moneda(e.pais)) + (v.reportado ? " reportado" : "") : ""}</span>
																		</li>
																	))}
																</ul>
															)}
														</td></tr>
													) : null}
												</React.Fragment>
											);
										})}
										<tr className="te-suma">
											<td /><td>Total</td>
											{modo === "semana" ? dias.map((d) => <td key={d} className="te-dia pd-num">{(() => { const s = c.filas.reduce((a, f) => a + (f.porDia[d] || 0), 0); return s ? num(s) : <i>·</i>; })()}</td>) : null}
											<td className="te-num pd-num"><b>{num(c.mat)}</b></td><td className="te-num te-monto pd-num">{dinero(c.monto, moneda(e.pais))}</td>
										</tr>
									</tbody>
								</table></div>
							)}
						</section>
					);
				})}
			</div>
		</div>
	);
};

const horaDe = (f: string) => { const [hh, mm] = (f.split(" ")[1] || "00:00").split(":").map(Number); return `${((hh + 11) % 12) + 1}:${pad(mm)} ${hh < 12 ? "AM" : "PM"}`; };
const diaCorto = (f: string) => { const d = deIso(f.slice(0, 10)); return DIAS_CORTO[d.getDay()] + " " + d.getDate(); };

export default TableroEquipos;
